import { useEffect, useRef, type CSSProperties } from 'react';
import {
  anchorHit, toPixelRects, type HighlightAnchor, type RenderPlan,
} from '../../core/page-geometry';
import type { PdfDocument, PdfTextContent } from '../pdf';

/**
 * One page of a continuously scrolling document: a box that is always the right size, and
 * three layers that exist only while the page is near the viewport.
 *
 * The split between those two halves is the whole design.
 *
 * **The box is always there, at its true size.** `MaterialViewer` plans every page's box from
 * `getViewport` before anything is painted — obtaining a page's dimensions does not require
 * rendering it — so a forty-page document has its real scroll height on the first frame. Get
 * this wrong and the scrollbar walks backwards under the teacher's thumb as pages paint,
 * which is the failure that makes continuous scroll feel broken rather than slow.
 *
 * **The canvas is not.** A letter page at fit-width in a 900 px column, at a device pixel
 * ratio of 2, is a 1,792 x 2,319 backing store — 16.6 MB, measured. Forty of those is two
 * thirds of a gigabyte and a tab the system kills, which is the argument that kept the pager
 * through pass 1. What makes continuous scroll affordable is
 * that only `live` pages hold one: the canvas and the text layer are *unmounted* outside the
 * window, and the cleanup below sets the backing store to 0 x 0 on the way out rather than
 * trusting a detached element to be collected before the next three are allocated.
 *
 * A page leaving the window also cancels whatever it was painting. A scroll fling crosses
 * thirty pages, and without that every one of them would queue a render that nothing is
 * waiting for.
 */

/** A highlight to draw on this page. `commentId` is null for the selection in hand. */
export interface PageHighlight {
  readonly commentId: string | null;
  readonly anchor: HighlightAnchor;
}

export interface PdfPageBoxProps {
  doc: PdfDocument;
  /** 1-based, and the number a teacher is shown. */
  pageNumber: number;
  pageCount: number;
  /** The box, and the two scales inside it. Recomputed on a zoom or a change of width. */
  plan: RenderPlan;
  /** Whether this page is inside the render window. */
  live: boolean;
  /** The material's title, for the canvas's label. */
  title: string;
  highlights: readonly PageHighlight[];
  focusedCommentId: string | null;
  /** A click or a keypress on a saved highlight. */
  onActivate: (commentId: string) => void;
  /** Whether this page had any text to select. Reported once per paint. */
  onText: (page: number, hasText: boolean) => void;
  /** A page pdf.js could not give us. The viewer states it; this component does not. */
  onUnreadable: () => void;
}

export default function PdfPageBox({
  doc, pageNumber, pageCount, plan, live, title,
  highlights, focusedCommentId, onActivate, onText, onUnreadable,
}: PdfPageBoxProps) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textLayerRef = useRef<HTMLDivElement | null>(null);

  /**
   * The callbacks, held rather than depended on — the same reason `MaterialViewer` holds its
   * own. A parent that passes an inline arrow hands this component new functions every render,
   * and in the dependency list below that is a repaint per render of a page that has not
   * changed.
   */
  const handlers = useRef({ onText, onUnreadable });
  handlers.current = { onText, onUnreadable };

  useEffect(() => {
    if (!live) return;
    const canvas = canvasRef.current;
    const layer = textLayerRef.current;
    if (canvas === null || layer === null) return;

    let alive = true;
    let painting: { cancel(): void } | null = null;
    let lettering: { cancel(): void } | null = null;

    // Emptied before anything is awaited, rather than only when the replacement is ready. A
    // new canvas arrives before its text does, and in that gap the old layout's spans sit over
    // the new one: a teacher selects a sentence that is not where they are pointing.
    layer.replaceChildren();

    void (async () => {
      let page;
      try {
        page = await doc.getPage(pageNumber);
      } catch {
        if (alive) handlers.current.onUnreadable();
        return;
      }
      if (!alive) return;

      // The backing store, in device pixels. The element's *layout* size is the page box's,
      // set from `plan` in the markup below — these are the two sizes it is fatal to conflate.
      canvas.width = plan.canvasWidth;
      canvas.height = plan.canvasHeight;

      let render: ReturnType<typeof page.render>;
      try {
        render = page.render({ canvas, viewport: page.getViewport({ scale: plan.renderScale }) });
      } catch {
        if (alive) handlers.current.onUnreadable();
        return;
      }
      painting = render;

      /*
       * The paint's outcome is subscribed to *now*, not after the text content arrives.
       *
       * A page can leave the window while `getTextContent()` is still in flight. The early
       * return below then leaves the effect without ever awaiting `render.promise`, the
       * cleanup cancels it, and pdf.js rejects a promise nobody is listening to — an
       * unhandled `RenderingCancelledException` in a teacher's console. With a pager that was
       * a corner case; with continuous scroll it is every fling, and it is exactly what the
       * browser run caught. `true` is a paint that finished, `false` one that did not.
       */
      const painted = render.promise.then(() => true, () => false);

      // The text layer, laid out in CSS pixels — `cssScale`, not `renderScale`. Getting that
      // wrong is the classic misalignment: a layer built at the backing store's scale is
      // exactly `dpr` times too large and lands its spans off the right-hand edge.
      const textViewport = page.getViewport({ scale: plan.cssScale });

      let content: PdfTextContent;
      try {
        content = await page.getTextContent();
      } catch {
        // A page whose text cannot be read is still a page worth looking at. The canvas
        // stands; only selection is lost, and the viewer says so.
        content = { items: [] };
      }
      if (!alive) return;

      handlers.current.onText(
        pageNumber,
        content.items.some((item) => (item.str ?? '').trim().length > 0),
      );

      const text = page.renderTextLayer({ container: layer, viewport: textViewport, textContent: content });
      lettering = text;
      // Cancelling a text layer rejects its promise, and scrolling past a page mid-layout is
      // now the ordinary way that happens — forty times in one fling. Handled here so it is
      // never an unhandled rejection in a teacher's console.
      void text.promise.catch(() => {});

      // A cancelled render rejects, and that is the normal path out of this effect once a page
      // can leave the window. Only a paint that failed while it was still wanted is a fault.
      if (!(await painted) && alive) handlers.current.onUnreadable();
    })();

    return () => {
      alive = false;
      // Still painting when the page leaves the window. Without this it paints into a detached
      // element and pdf.js reports "Cannot use the same canvas during multiple render()".
      painting?.cancel();
      lettering?.cancel();
      // And the backing store goes now rather than whenever the collector gets to it. This is
      // the line that makes the window a memory bound instead of a hope: three live canvases
      // at any scroll position, forty-page document or four.
      canvas.width = 0;
      canvas.height = 0;
    };
  }, [doc, pageNumber, plan, live]);

  /**
   * A click on a highlight, decided by where it landed.
   *
   * The highlight layer never takes the pointer — it sits *under* pdf.js's text layer, so the
   * words a highlight covers can still be selected and copied, which an overlay of clickable
   * elements would quietly take away. So the page box asks the arithmetic whether the click
   * was inside one, and a drag that ended in a selection is not a click at all.
   */
  const onClick = (event: { clientX: number; clientY: number }) => {
    const box = boxRef.current;
    if (box === null || highlights.length === 0) return;

    const selection = typeof window === 'undefined' ? null : window.getSelection();
    if (selection !== null && !selection.isCollapsed) return;

    const rect = box.getBoundingClientRect();
    const saved = highlights.filter((highlight) => highlight.commentId !== null);
    const index = anchorHit(
      saved.map((highlight) => highlight.anchor),
      { x: event.clientX - rect.left, y: event.clientY - rect.top },
      { width: rect.width || plan.cssWidth, height: rect.height || plan.cssHeight },
    );
    if (index === -1) return;
    onActivate(saved[index]?.commentId as string);
  };

  const box = { width: plan.cssWidth, height: plan.cssHeight };

  return (
    <div
      ref={boxRef}
      className="viewer-page"
      data-page={pageNumber}
      onClick={onClick}
      style={{
        width: `${plan.cssWidth}px`,
        height: `${plan.cssHeight}px`,
        // pdf.js's text layer sizes itself, and positions every span, from this one variable.
        // It is what keeps the words pinned to the picture through a zoom.
        '--total-scale-factor': String(plan.cssScale),
      } as CSSProperties}
    >
      {live && (
        <canvas
          ref={canvasRef}
          className="viewer-canvas"
          // This is the picture of the page and is labelled as one. The words are the text
          // layer's job, two elements down.
          role="img"
          aria-label={`${title} (PDF), page ${pageNumber} of ${pageCount}`}
        />
      )}

      {live && (
        <div className="viewer-highlights">
          {highlights.map((highlight, index) => (
            <span key={highlight.commentId ?? `pending-${index}`} className="viewer-highlight-group">
              {toPixelRects(highlight.anchor.rects, box).map((rect, part) => (
                <span
                  key={part}
                  className={
                    `viewer-highlight${highlight.commentId === null ? ' viewer-highlight-pending' : ''}`
                    + (highlight.commentId !== null && highlight.commentId === focusedCommentId
                      ? ' viewer-highlight-focused' : '')
                  }
                  style={{
                    left: `${rect.x}px`, top: `${rect.y}px`,
                    width: `${rect.w}px`, height: `${rect.h}px`,
                  }}
                  // The first rect of a saved highlight is the keyboard's way in. The layer
                  // takes no pointer events, so a mouse goes to the words underneath and the
                  // click is hit-tested above; a keyboard has no position to hit-test, so it
                  // gets a focusable element that says what it does.
                  {...(part === 0 && highlight.commentId !== null ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': `Comment on “${highlight.anchor.quote}”`,
                    onKeyDown: (event: { key: string; preventDefault: () => void }) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      onActivate(highlight.commentId as string);
                    },
                  } : {})}
                />
              ))}
            </span>
          ))}
        </div>
      )}

      {live && (
        /*
         * The selectable copy of the page. pdf.js fills it with one transparent span per text
         * run, positioned over the matching pixels. It is what a mouse selects, what Cmd-C
         * copies and what puts the page's words into the accessibility tree; without it the
         * canvas above is a picture of some words and nothing on this screen knows what they
         * say.
         */
        <div ref={textLayerRef} className="viewer-text-layer textLayer" />
      )}
    </div>
  );
}
